<script setup>
    import { defineAsyncComponent, ref, onMounted,computed } from 'vue'
    import { useAuditStore } from '@/stores/audit'
    import { useUsersStore } from '@/stores/users'
    import { format } from 'date-fns'

    import useIframeControl from '@/hooks/iframeAudit'
    import useToggleTheme from '@/hooks/useToggleTheme'

    const SearchAudit = defineAsyncComponent(() => import('@/views/Audit/SearchAudit.vue'))
    const LoadingFull = defineAsyncComponent(() => import('@/components/Utils/LoadingFull.vue'))
    const NotFoundInfo = defineAsyncComponent(() => import('@/components/Utils/NotFoundInfo.vue'))
    const MessageLoading = defineAsyncComponent(() => import('@/components/WebChatInternal/Message/MessageLoading.vue'))
    const ChatMessage = defineAsyncComponent(() => import('@/components/WebChatInternal/ChatMessage.vue'))
    const ScrollBottom = defineAsyncComponent(() => import('@/components/InfiniteScroller/ScrollBottom.vue'))
    const Icon = defineAsyncComponent(() => import('@/components/Icon/IndexIcon.vue'))

    const iframe = useIframeControl()
    const auditStore = useAuditStore()
    const usersStore = useUsersStore()
    const { themeDark } = useToggleTheme()

    const open = ref(true)
    const isLoadingPreview = ref(false)
    const isSearchMessage = ref(false)
    const isFetchMessage = ref(false)
    const toggleScroll = ref(false)
    const isScrollBottom = ref(false)
    const initialLoad = ref(true)
    const isScrollBottomBehavior = ref('auto')
    const search = ref(null)

    const bgThemeImg = computed(() => themeDark() ? 'bg-img-bg-chat-dark' : 'bg-img-bg-chat-light')
    const messages = computed(() => auditStore.messages)
    const hasMoreMessages = computed(() => auditStore.has_more_messages)
    const isLoadingLoader = computed(() => isSearchMessage.value ? isSearchMessage.value : isFetchMessage.value )
    const sender = computed(() => {
        if (search.value?.sender) {
            const user = usersStore.getById(parseInt(search.value.sender))
            return user.first_name + ' ' + user.last_name
        }
        return ''
    } )
    const recipient = computed(() => {
        if (search.value?.recipient) {
            const user = usersStore.getById(parseInt(search.value.recipient))
            return user.first_name + ' ' + user.last_name
        }
        return ''
    })

    const datestart = computed(() => {
        if (search.value?.datestart) return format(new Date(search.value.datestart), 'dd/MM/yyyy')
        return ''
    })

    const messageTop = computed(() => {
        if(isSearchMessage.value || isFetchMessage.value ) return 'Aguarde estamos carregando as mensagens'
        return 'Não existem mais mensagens para carregar'
    })
    const isGroup = computed(() => {
        if (search.value?.with === 'group') return true
        return false
    })

    const statusAudit = computed(() => {
        if(!search.value) return {
            title: 'Para auditar uma conversa, selecione os usuários e clique em pesquisar',
            type: 'info'
        }
        if(isSearchMessage.value) return {
            title: 'Aguarde estamos carregando as mensagens',
            type: 'loading'
        }
        if(!isLoadingLoader.value && messages.value.length === 0 && search.value?.sender != '') {
            let tagline = 'Selecione outro tipo ou outro grupo para auditar as mensagens'
            if( search.value.with === 'user') {
                tagline = `de ${recipient.value}  para ${sender.value} a partir de ${datestart.value} `
            }
            return {
                title: 'Opps! não encontramos nenhuma mensagem',
                tagline: tagline,
                type: 'error'
            }
        }

        return {
            title: 'mensagens carregadas',
            type: 'success'
        }
    })

    async function handleInitialize() {
        try {
            isLoadingPreview.value = true
            await auditStore.initialize()
            open.value = true
        } catch (error) {
            console.error(error)
            router.push({ name: 'NotFound', params: { to: 'app' } })
        } finally {
            isLoadingPreview.value = false
        }
    }

    async function handleFetchMessage () {
        if (search.value && !isFetchMessage.value && hasMoreMessages.value && !isSearchMessage.value) {
            try {
                isFetchMessage.value = true
                await auditStore.search(search.value)
            } catch (error) {
                if (error.message === 'not a page filter') {
                    isNotMessage.value = true
                }
            }
            finally{
                setTimeout(() => isFetchMessage.value = false, 1000)
            }
        }
    }

    async function handleSearch(payload) {
        try {
            if(isSearchMessage.value) return
            search.value = payload
            isSearchMessage.value = true
            auditStore.resetMessages()
            await auditStore.search(payload,'search')
        } catch (error) {
            isSearchMessage.value = false
            if (error.message === 'not a page filter') {
                isNotMessage.value = true
            }
        }
        finally{
            setTimeout(() => {
                isSearchMessage.value = false
                initialLoad.value = false
            }, 1000)
        }

    }

    onMounted(() => {
        iframe.updateCoreValuesOnStore()
        handleInitialize()
    })

</script>
<template>
    <div class="fixed bottom-0 h-full w-full animate__animated animate__fadeInUp animate__faster left-0 print:static">
        <div
            v-if="isLoadingPreview"
            class="absolute top-0 left-0 z-40 w-full h-full bg-neutral-100 dark:bg-neutral-600 "
        >
            <LoadingFull />
        </div>
        <div
            v-else
            class="flex flex-col w-full h-full items-end print:h-auto">
            <div
                class="grow w-full overflow-hidden justify-end bg-white dark:bg-neutral-700 print:overflow-visible print:bg-white dark:print:bg-white"
            >
                <div class="flex flex-col h-full rounded-r-md">
                    <SearchAudit
                        :loading="isSearchMessage"
                        @search="handleSearch"
                    />
                    <div class="grow overflow-auto print:overflow-visible">
                        <div class="flex print:block h-full relative print:h-auto print:static">
                            <div
                                class="flex flex-col bg-neutral-200 dark:bg-neutral-700 w-full border-t-md rounded-t-md print:bg-white dark:print:bg-white print:bg-none"
                                :class="bgThemeImg"
                            >
                                <div
                                    v-if="statusAudit.type !== 'success'"
                                    class="flex flex-col w-full px-6 py-2 h-full">
                                    <NotFoundInfo
                                        v-if="statusAudit.type === 'info'"
                                        class="items-center justify-center"
                                        icon="Chat"
                                        :title="statusAudit.title"
                                        tagline=""
                                    />
                                    <NotFoundInfo
                                        v-if="statusAudit.type === 'loading'"
                                        class="items-center justify-center"
                                        icon="Loading"
                                        :title="statusAudit.title"
                                        tagline=""
                                    />
                                    <NotFoundInfo
                                        v-if="statusAudit.type === 'error'"
                                        class="items-center justify-center"
                                        icon="Chat"
                                        :title="statusAudit.title"
                                        :tagline="statusAudit.tagline"
                                    />
                                </div>
                                <ScrollBottom
                                    v-else
                                    :value="messages.length"
                                    :loadingMore="isFetchMessage"
                                    :canLoadMore="hasMoreMessages"
                                    :initialLoad="initialLoad"
                                    :scrollToBottom="isScrollBottom"
                                    :scroll-behavior="isScrollBottomBehavior"
                                    marginInterSectionObserver="10px"
                                    @load="handleFetchMessage"
                                >
                                    <template #initial>
                                        <MessageLoading
                                            :loading="isLoadingLoader"
                                            :text="messageTop"
                                            margin="mb-4"
                                        />
                                    </template>
                                    <template #loading>
                                        <MessageLoading
                                            :loading="isLoadingLoader"
                                            :text="messageTop"
                                            margin="mb-4"
                                        />
                                    </template>
                                    <template #canLoadMore>
                                        <MessageLoading
                                            v-if="!hasMoreMessages && messages.length > 24"
                                            :loading="isSearchMessage"
                                            text="Não existem mais conversas para carrregar"
                                            margin="mb-4"
                                        />
                                    </template>
                                    <template #items>
                                        <div class="mt-8" />
                                        <ChatMessage
                                            :loading="isFetchMessage"
                                            :content="messages"
                                            :startDate="true"
                                            :display-avatar="isGroup"
                                            :display-name="isGroup"
                                            :audit="true"
                                        />

                                    </template>
                                </ScrollBottom>
                                <button
                                    v-show="toggleScroll"
                                    class="absolute bottom-28 min-w-auto w-9 h-9 bg-chat-primary right-2 rounded-full hover:bg-chat-primary text-white font-semibold flex items-center justify-center"
                                >
                                    <Icon
                                        size="14"
                                        name="ChevronDown"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style module>

@media print {
    * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        -ms-overflow-style: none !important;
        box-shadow: none !important;
    }

    *::-webkit-scrollbar {
        display: none !important;
    }

    div.whitespace-pre-wrap.w-full.break-words {
        color: #000 !important;
    }

    audio {
        display: inline-block !important;
    }
}
</style>
